import { Lightbulb, MessageCircle } from 'lucide-react';
import { FeatureGuard } from '@/hooks/use-plan-features';
import { FeatureBlockedMessage } from '@/components/subscription/FeatureBlockedPage';
import { FEATURES_ACCOUNT } from '@/config/features';
import rogerioFoto from '@/assets/mentoria/rogerio-olegario.png';
import andreFoto from '@/assets/mentoria/andre-frohlich.png';

// ── Paleta oficial do site librattum.com.br ────────────────────────────────
const BRAND = {
  green: '#00a499',
  greenDark: '#00776f',
  greenDeep: '#00534d',
  gold: '#f2a900',
} as const;

// ── Configuração da página (fácil de editar) ───────────────────────────────
const WHATSAPP_ROGERIO = 'https://wa.me/5561981735771';
const WHATSAPP_ANDRE = 'https://wa.me/5547984057829';
const HORARIO_ATENDIMENTO =
  'De segunda a sexta-feira, das 09h00 às 12h00 e das 14h00 às 18h00.';

// Mensagem pré-preenchida ao abrir o WhatsApp.
const MENSAGEM_AGENDAMENTO =
  'Olá! Vim da plataforma do Librattum e gostaria de saber mais sobre a Mentoria Librattum.';

/** Monta o link do WhatsApp já com a mensagem pré-preenchida. */
const waLink = (numero: string) =>
  `${numero}?text=${encodeURIComponent(MENSAGEM_AGENDAMENTO)}`;

/** Botão "Agendar Mentoria" em pílula verde da marca. */
function AgendarButton({ href }: Readonly<{ href: string }>) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors"
      style={{ backgroundColor: BRAND.green }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.greenDark)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.green)}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
        <MessageCircle className="h-3.5 w-3.5" />
      </span>
      Agendar Mentoria
    </a>
  );
}

/** Plaquinha com nome + cargo, sobreposta à foto. */
function NamePlate({ nome, cargo, className = '' }: Readonly<{ nome: string; cargo: string; className?: string }>) {
  return (
    <div
      className={`rounded-lg px-5 py-2 text-center shadow-lg ${className}`}
      style={{ backgroundColor: BRAND.greenDark }}
    >
      <p className="text-sm font-semibold text-white sm:text-base">{nome}</p>
      <p className="text-xs italic text-white/75">{cargo}</p>
    </div>
  );
}

function MentoriaContent() {
  return (
    <div className="min-h-full bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-6 sm:px-6">
        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden rounded-2xl shadow-md">
          {/* fundo verde da marca + textura + acento dourado */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(110deg, ${BRAND.greenDeep} 0%, ${BRAND.greenDark} 45%, ${BRAND.green} 100%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.35) 1px, transparent 0)',
              backgroundSize: '26px 26px',
            }}
          />
          <div
            className="absolute -right-16 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full blur-3xl"
            style={{ backgroundColor: `${BRAND.gold}40` }}
          />

          <div className="relative flex min-h-[320px] flex-col justify-center gap-5 p-8 sm:p-12 lg:max-w-2xl">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-sm font-medium text-white backdrop-blur">
              <Lightbulb className="h-4 w-4" style={{ color: BRAND.gold }} />
              Mentoria Librattum
            </div>
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
              Mentoria Librattum
            </h1>
            <p className="max-w-xl text-base leading-7 text-white/90">
              Utilize todo o potencial da nossa ferramenta. Converse com os nossos consultores e
              agende a sua mentoria.
            </p>
          </div>
        </section>

        {/* ── O QUE É? (Rogério, foto à esquerda) ─────────────────────────── */}
        <section className="relative overflow-visible rounded-2xl border border-border/60 bg-card shadow-sm">
          <div className="grid items-center gap-6 p-6 sm:p-8 md:grid-cols-[280px,1fr]">
            <div className="relative mx-auto flex w-full max-w-[260px] justify-center md:-my-12">
              <img
                src={rogerioFoto}
                alt="Rogério Olegário"
                className="w-52 self-end object-contain drop-shadow-xl sm:w-60"
              />
              <NamePlate
                nome="Rogério Olegário"
                cargo="Consultor Financeiro Pessoal"
                className="absolute bottom-0 left-1/2 w-[230px] -translate-x-1/2 translate-y-2"
              />
            </div>

            <div className="space-y-4 pt-6 md:pt-0">
              <h2 className="text-2xl font-bold" style={{ color: BRAND.greenDark }}>
                Mentoria Librattum — O que é?
              </h2>
              <p className="text-[15px] leading-7 text-muted-foreground">
                Para quem serve? É uma conversa para sanar dúvidas sobre o programa e a metodologia
                utilizada. É para você que está iniciando sua jornada de organização financeira e
                quer aproveitar ao máximo todos os recursos do Librattum.
              </p>
              <AgendarButton href={waLink(WHATSAPP_ROGERIO)} />
            </div>
          </div>
        </section>

        {/* ── COMO FUNCIONA? (André, foto à direita) ─────────────────────── */}
        <section className="relative overflow-visible rounded-2xl border border-border/60 bg-card shadow-sm">
          <div className="grid items-center gap-6 p-6 sm:p-8 md:grid-cols-[1fr,280px]">
            <div className="order-2 space-y-4 pt-6 text-left md:order-1 md:pt-0 md:text-right">
              <h2 className="text-2xl font-bold" style={{ color: BRAND.greenDark }}>
                Como funciona?
              </h2>
              <p className="text-[15px] leading-7 text-muted-foreground">
                É uma conversa on-line com um de nossos consultores, com duração de até 30 minutos e
                que precisa ser agendada previamente, conforme a disponibilidade de horário.
              </p>
              <div className="flex md:justify-end">
                <AgendarButton href={waLink(WHATSAPP_ANDRE)} />
              </div>
            </div>

            <div className="relative order-1 mx-auto flex w-full max-w-[260px] justify-center md:order-2 md:-my-12">
              <img
                src={andreFoto}
                alt="André Fröhlich"
                className="w-52 self-end object-contain drop-shadow-xl sm:w-60"
              />
              <NamePlate
                nome="André Fröhlich"
                cargo="Planejador Financeiro Pessoal"
                className="absolute bottom-0 left-1/2 w-[230px] -translate-x-1/2 translate-y-2"
              />
            </div>
          </div>
        </section>

        {/* ── HORÁRIO DE ATENDIMENTO ─────────────────────────────────────── */}
        <section className="relative mt-2 rounded-2xl border border-border/60 bg-card px-6 pb-8 pt-9 text-center shadow-sm">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <span
              className="inline-block rounded-lg px-6 py-2 text-sm font-semibold text-white shadow-md"
              style={{ backgroundColor: BRAND.greenDark }}
            >
              Horário de atendimento
            </span>
          </div>
          <p className="text-[15px] leading-7 text-muted-foreground">{HORARIO_ATENDIMENTO}</p>
        </section>
      </div>
    </div>
  );
}

export default function MentoriaPage() {
  return (
    <FeatureGuard
      feature={FEATURES_ACCOUNT.MENTORIA_LIBRATTUM}
      fallback={<FeatureBlockedMessage featureName="Mentoria Librattum" />}
    >
      <MentoriaContent />
    </FeatureGuard>
  );
}
